import { useDispatch, useSelector, useStore } from 'react-redux';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Stack, SxProps, Theme, Typography } from '@mui/material';
import Header from './header';
import WelcomePage from './welcome-page';
import Footer from './footer';
import ActiveChat from 'app/concierge/panel/active-chat';
import RecentChats from 'app/concierge/panel/recent-chats';
import { Resizable } from 're-resizable';
import {
    closeChat as setCloseChat,
    setConciergeWidgetWidth,
    setSelectedDomains,
    setCustomStyles,
    setIsAppPanel,
    clearNewRequestMessage,
    clearPrependContext,
} from 'store/conciergeSlice';
import { hide, clearNewChat, setFromSearch } from 'store/conciergeSlice';
import { setWaitingConcierge } from 'store/globalSearchSlice';
import { merge } from 'lodash';
import { tokenFormUpdatePropertyValue } from 'tools/tools';
import { useConciergeChat } from '../hooks/useConciergeChat';
import { useHasConciergeDomains } from '../hooks/useHasConciergeDomains';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';
import { dispatchCustomEvent } from 'tools/events';
import type { RootState } from 'store/store';

import defaultStyles from './styles';

export interface ICustomStyles {
    panel?: SxProps<Theme>;
    welcomePage?: SxProps<Theme>;
    header?: SxProps<Theme>;
    footer?: SxProps<Theme>;
    footerDomainSelector?: SxProps<Theme>;
    activeChat?: SxProps<Theme>;
    activeChatMessage?: SxProps<Theme>;
    activeChatMessageResultItem?: SxProps<Theme>;
}

interface IProps {
    welcomeIcon?: string;
    welcomeIconWidth?: number;
    welcomeIconHeight?: number;
    botIcon?: string;
    title?: string;
    description?: string;
    startNewChatAfter?: number;
    showSourceSelector?: boolean;
    displayModelName?: boolean;
    suggestedPrompts?: string[];
    showSearchRequest?: boolean;
    includeContentSearch?: boolean;
    includeHelpDocs?: boolean;
    includeGlossaryTerms?: boolean;
    includeCustomFaq?: boolean;
    availableDomains?: string[];
    selectedDomains?: string[];
    isAppPanel?: boolean;
    customStyles?: ICustomStyles;
    loadMore?: boolean;
    extraUrlParams?: { [key: string]: string };
    useInfoButton?: boolean;
    hideHeader?: boolean;
    autoFocus?: boolean;
    getContext?: () => any | Promise<any>;
    customInstructions?: {
        mcp_post_processing?: string;
        llm_comment?: string;
    };
    onAction?: (
        action: string,
        payload: any,
    ) => Promise<{ success: boolean; message?: string }> | { success: boolean; message?: string };
}

const RESIZE_PAUSE_MS = 100;

export default function ConciergePanel({
    welcomeIcon,
    title,
    description,
    suggestedPrompts,
    botIcon,
    startNewChatAfter,
    displayModelName,
    showSourceSelector,
    welcomeIconHeight,
    welcomeIconWidth,
    showSearchRequest,
    availableDomains,
    includeContentSearch,
    includeCustomFaq,
    includeGlossaryTerms,
    includeHelpDocs,
    selectedDomains,
    isAppPanel,
    customStyles,
    loadMore,
    extraUrlParams,
    useInfoButton = false,
    hideHeader = false,
    autoFocus = true,
    getContext,
    customInstructions,
    onAction,
}: IProps) {
    const dispatch = useDispatch();
    const { t } = useBundleTranslation(['app/concierge/widget']);

    const styles = useMemo(() => {
        return merge({}, defaultStyles, customStyles?.panel) as typeof defaultStyles;
    }, [customStyles]);

    const visible = useSelector((state: any) => state.concierge.visible);
    // Read without subscribing: the panel only needs the width as its starting size when it opens, and
    // re-rendering the chat on a mid-drag width update would only lengthen that update.
    const store = useStore<RootState>();
    const newChat = useSelector((state: any) => state.concierge.newChat);
    const newRequestMessage = useSelector((state: any) => state.concierge.newRequestMessage);
    const prependContext = useSelector((state: any) => state.concierge.prependContext);
    const closeChat = useSelector((state: any) => state.concierge.closeChat);

    const [activeTab, setActiveTab] = useState('concierge');

    const { hasDomains, isLoading: isDomainsLoading } = useHasConciergeDomains();

    const {
        dialog,
        setDialog,
        loading,
        activeChat,
        setActiveChat,
        onRequestSubmit,
        startNewChat,
        onAbort,
        domainsData,
        toolsData,
        onResendRequest,
        setLastInteraction,
        clearActiveChatRequest,
        silentAbort,
    } = useConciergeChat(
        {
            startNewChatAfter,
            includeContentSearch,
            includeHelpDocs,
            includeGlossaryTerms,
            includeCustomFaq,
            availableDomains,
            selectedDomains,
            isAppPanel,
            extraUrlParams,
            getContext,
            customInstructions,
        },
        visible,
    );

    useEffect(() => {
        if (newChat !== null && domainsData && toolsData) {
            silentAbort();
            clearActiveChatRequest();
            setDialog([]);
            setActiveChat(Date.now());
            dispatch(clearNewChat());
            if (newChat) {
                onRequestSubmit(newChat);
            }
        }
    }, [newChat, domainsData, toolsData, clearActiveChatRequest, dispatch, silentAbort, onRequestSubmit]);

    useEffect(() => {
        if (newRequestMessage && domainsData && toolsData) {
            if (!activeChat) {
                setActiveChat(Date.now());
            }
            dispatch(clearNewRequestMessage());
            onRequestSubmit(newRequestMessage);
        }
    }, [newRequestMessage, domainsData, toolsData, activeChat, onRequestSubmit, dispatch]);

    useEffect(() => {
        if (prependContext?.showInChat && prependContext.text && dialog.length === 0 && activeChat) {
            const contextMessage = {
                uid: `prepend-context-${Date.now()}`,
                type: 'response' as const,
                parts: [
                    {
                        message: prependContext.text,
                        type: 'markdown' as const,
                        source: 'frontend' as const,
                    },
                ],
            };
            setDialog([contextMessage]);
        }
    }, [prependContext, dialog.length, activeChat]);

    const onClose = () => {
        setDialog([]);
        setActiveChat(0);
        dispatch(setFromSearch(false));
        dispatch(hide());
        dispatch(setCloseChat(false));
        dispatch(clearNewChat());
        dispatch(setWaitingConcierge(false));
        dispatch(clearPrependContext());
    };

    useEffect(() => {
        if (closeChat) {
            onClose();
        }
    }, [closeChat]);

    useEffect(() => {
        dispatch(setCustomStyles(customStyles));
        dispatch(setIsAppPanel(isAppPanel));

        //@ts-ignore
        if (!window.tokenFormUpdatePropertyValue) {
            //@ts-ignore
            window.tokenFormUpdatePropertyValue = tokenFormUpdatePropertyValue;
        }

        return () => {
            //@ts-ignore
            delete window.tokenFormUpdatePropertyValue;
        };
    }, []);

    const resizeFrameRef = useRef<number | null>(null);
    const resizePauseRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const dragStartWidthRef = useRef(0);
    // Width under the cursor while a drag is in progress; null when no drag is.
    const dragWidthRef = useRef<number | null>(null);
    const emittedWidthRef = useRef<number | null>(null);

    const emitWidth = useCallback(
        (width: number, resizeState: 'dragging' | 'released') => {
            if (!isAppPanel) return;
            dispatchCustomEvent('mi-concierge-panel', {
                action: 'panel-visibility-changed',
                payload: { isOpen: true, width, resizeState },
            });
        },
        [isAppPanel],
    );

    const cancelResizeTimers = () => {
        if (null !== resizeFrameRef.current) {
            cancelAnimationFrame(resizeFrameRef.current);
            resizeFrameRef.current = null;
        }
        if (null !== resizePauseRef.current) {
            clearTimeout(resizePauseRef.current);
            resizePauseRef.current = null;
        }
    };

    useEffect(() => cancelResizeTimers, []);

    // Closed mid-drag: Resizable unmounts without onResizeStop, so the dragged width is saved here, before
    // the close is reported.
    useLayoutEffect(() => {
        if (visible || null === dragWidthRef.current) return;
        cancelResizeTimers();
        dispatch(setConciergeWidgetWidth(dragWidthRef.current));
        dragWidthRef.current = null;
    }, [visible]);

    return visible ? (
        <Stack sx={styles.container}>
            <Resizable
                defaultSize={{
                    width: store.getState().concierge.width,
                    height: '100%',
                }}
                className={'hierarchy-resize-container'}
                minWidth={400}
                maxWidth={660}
                onResizeStart={(event, direction, elementRef) => {
                    elementRef.classList.add('resizing-active');
                    dragStartWidthRef.current = elementRef.offsetWidth;
                    dragWidthRef.current = elementRef.offsetWidth;
                    emittedWidthRef.current = elementRef.offsetWidth;
                }}
                onResize={(event, direction, elementRef, delta) => {
                    dragWidthRef.current = Math.round(dragStartWidthRef.current + delta.width);
                    if (null === resizeFrameRef.current) {
                        resizeFrameRef.current = requestAnimationFrame(() => {
                            resizeFrameRef.current = null;
                            const width = dragWidthRef.current;
                            if (null === width || width === emittedWidthRef.current) return;
                            emittedWidthRef.current = width;
                            emitWidth(width, 'dragging');
                        });
                    }
                    // Page content follows only when the drag pauses: re-laying out a heavy page (grids) costs
                    // ~100 ms, which stalls the drag if done every frame.
                    if (null !== resizePauseRef.current) clearTimeout(resizePauseRef.current);
                    resizePauseRef.current = setTimeout(() => {
                        resizePauseRef.current = null;
                        if (null !== dragWidthRef.current) dispatch(setConciergeWidgetWidth(dragWidthRef.current));
                    }, RESIZE_PAUSE_MS);
                }}
                onResizeStop={(event, direction, elementRef) => {
                    cancelResizeTimers();
                    dragWidthRef.current = null;
                    elementRef.classList.remove('resizing-active');
                    dispatch(setConciergeWidgetWidth(elementRef.offsetWidth));
                    emitWidth(elementRef.offsetWidth, 'released');
                }}
                handleComponent={{
                    left: <Stack sx={styles.resizeHandler} />,
                }}
                handleStyles={{
                    left: styles.resizeStyle,
                }}
                enable={{
                    top: false,
                    right: false,
                    bottom: false,
                    left: true,
                    topRight: false,
                    bottomRight: false,
                    bottomLeft: false,
                    topLeft: false,
                }}
            >
                {!hideHeader && (
                    <Header
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        setLastInteraction={setLastInteraction}
                        botIcon={botIcon}
                    />
                )}
                {activeTab == 'concierge' && !isDomainsLoading && !hasDomains && (
                    <Stack sx={{ flex: 1, alignItems: 'center', justifyContent: 'center', p: 3 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                textAlign: 'center',
                                gap: 1,
                                p: 3,
                                borderRadius: '8px',
                                border: '1px solid',
                                borderColor: 'divider',
                                backgroundColor: (theme) =>
                                    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                            }}
                        >
                            <Box sx={{ fontSize: '32px', color: 'warning.main', lineHeight: 1 }}>
                                <IconMi icon="info-circle" />
                            </Box>
                            <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>{t('no_sources_title')}</Typography>
                            <Typography sx={{ fontSize: '13px', color: 'text.secondary', maxWidth: '280px' }}>
                                {t('no_sources')}
                            </Typography>
                        </Box>
                    </Stack>
                )}
                {activeTab == 'concierge' && (isDomainsLoading || hasDomains) && (
                    <>
                        {!activeChat ? (
                            <WelcomePage
                                setActiveChat={setActiveChat}
                                setActiveChatRequest={onRequestSubmit}
                                welcomeIcon={welcomeIcon}
                                suggestedPrompts={suggestedPrompts}
                                title={title}
                                description={description}
                                welcomeIconWidth={welcomeIconWidth}
                                welcomeIconHeight={welcomeIconHeight}
                            />
                        ) : (
                            <ActiveChat
                                key={activeChat}
                                dialog={dialog}
                                id={activeChat}
                                loading={loading}
                                showSearchRequest={showSearchRequest}
                                botIcon={botIcon}
                                loadMore={loadMore}
                                onUpdateDialog={setDialog}
                                useInfoButton={useInfoButton}
                                onResendRequest={onResendRequest}
                                onAction={onAction}
                            />
                        )}
                        <Footer
                            activeChat={activeChat}
                            setActiveChat={setActiveChat}
                            setActiveChatRequest={onRequestSubmit}
                            startNewChat={startNewChat}
                            onAbort={onAbort}
                            loading={loading}
                            loadingToSendNewRequestMessage={newRequestMessage && !domainsData && !toolsData}
                            showSourceSelector={showSourceSelector}
                            displayModelName={displayModelName}
                            autoFocus={autoFocus}
                        />
                    </>
                )}
                {activeTab == 'recent' && (
                    <RecentChats setActiveChat={setActiveChat} setActiveChatRequest={onRequestSubmit} />
                )}
            </Resizable>
        </Stack>
    ) : null;
}
